import { formatInTimeZone } from 'date-fns-tz';

import { type ApiQueryParamsType } from '@libraries/typescript/api_client';

export const OUTREACH_ANALYTICS_TIMEZONE = 'America/New_York';

// Auto-refresh cadence for the live dashboard sections (hero + each tab).
export const OUTREACH_REFRESH_INTERVAL_MS = 60_000;

// Office-TV wait thresholds (seconds) that color the KPI, stat, and run badges
// green/amber/red. Calibrated to the current prod distribution (typical avg
// wait ~150s): under amber reads healthy, amber-to-red is a slowdown, past red
// is a problem visible from across the room.
export const TV_WAIT_AMBER_S = 180;
export const TV_WAIT_RED_S = 360;

// Time-to-first-recommendations thresholds (seconds). Healthy post-prefetch
// avg is ~39s; a regression to pre-prefetch behavior (~54s) reads amber.
export const TV_RECS_AMBER_S = 50;
export const TV_RECS_RED_S = 90;

// Frozen props contract every tab body renders against: the tab does its own
// data fetching from these two inputs. The route owns org + window selection.
export type OutreachTabProps = {
    readonly organizationIds: Array<string>;
    readonly window: OutreachWindowSelection;
    readonly urgency: OutreachUrgencyFilter;
};

export type OutreachUrgencyFilter = NonNullable<
    ApiQueryParamsType<'/admin/outreach-observability', 'get'>['urgency']
>;

// Live-section window the dashboard can request. Must stay in sync with the
// backend OutreachLiveWindow in libraries/python/outreach_run_analytics; the
// hook's query param is type-checked against the generated client so a drift
// surfaces at compile time.
export type OutreachLiveWindow =
    | 'trailing_24h'
    | 'today'
    | 'trailing_3d'
    | 'trailing_7d'
    | 'trailing_14d';

// An explicit [start, end) range selected in the dashboard, as ISO instants.
// The API rejects ranges longer than MAX_OUTREACH_CUSTOM_RANGE_MS.
export type OutreachCustomRange = {
    readonly start: string;
    readonly end: string;
};

// The dashboard's window selection: a named preset or a custom range.
export type OutreachWindowSelection = OutreachLiveWindow | OutreachCustomRange;

// Longest custom range the dashboard may request. Must stay in sync with
// MAX_CUSTOM_WINDOW_DAYS in outreach_run_analytics.
export const MAX_OUTREACH_CUSTOM_RANGE_DAYS = 14;
export const MAX_OUTREACH_CUSTOM_RANGE_MS =
    MAX_OUTREACH_CUSTOM_RANGE_DAYS * 24 * 60 * 60 * 1000;

export function isCustomOutreachRange(
    selection: OutreachWindowSelection,
): selection is OutreachCustomRange {
    return typeof selection !== 'string';
}

// Mirror of _scale_preset in outreach_run_analytics: a custom range borrows
// the payload-scale settings (histogram bucket size, outlier-feed cap) of the
// shortest preset that covers its span, keeping the client in lockstep with
// the backend's choice for the same range.
function scalePreset(selection: OutreachWindowSelection): OutreachLiveWindow {
    if (!isCustomOutreachRange(selection)) {
        return selection;
    }
    const dayMs = 24 * 60 * 60 * 1000;
    const spanMs = Date.parse(selection.end) - Date.parse(selection.start);
    if (spanMs <= dayMs) {
        return 'trailing_24h';
    }
    if (spanMs <= 3 * dayMs) {
        return 'trailing_3d';
    }
    if (spanMs <= 7 * dayMs) {
        return 'trailing_7d';
    }
    return 'trailing_14d';
}

// Single source of truth for the intraday outlier histogram's bucket size,
// shared by the histogram and its feed-window filter / range labels. Must stay
// in sync with _INTRADAY_BUCKET_MIN in outreach_run_analytics.
export const OUTREACH_BUCKET_MS_BY_WINDOW: Readonly<
    Record<OutreachLiveWindow, number>
> = {
    trailing_24h: 30 * 60 * 1000,
    today: 30 * 60 * 1000,
    trailing_3d: 60 * 60 * 1000,
    trailing_7d: 120 * 60 * 1000,
    trailing_14d: 240 * 60 * 1000,
};

// Must stay in sync with _MAX_OUTLIER_RUNS_BY_WINDOW in
// outreach_run_analytics. The histogram remains uncapped.
export const OUTLIER_FEED_CAP_BY_WINDOW: Readonly<
    Record<OutreachLiveWindow, number>
> = {
    trailing_24h: 50,
    today: 50,
    trailing_3d: 100,
    trailing_7d: 200,
    trailing_14d: 400,
};

export const DEFAULT_OUTREACH_LIVE_WINDOW: OutreachLiveWindow = 'trailing_24h';

// `label` is the dropdown option text; `summary` is the compact phrase reused
// in inline copy ("Outliers · {summary}", "Runs ({summary})").
export const OUTREACH_LIVE_WINDOW_OPTIONS: ReadonlyArray<{
    readonly value: OutreachLiveWindow;
    readonly label: string;
    readonly summary: string;
}> = [
    {
        value: 'trailing_24h',
        label: 'Trailing 24 hours',
        summary: 'trailing 24h',
    },
    { value: 'today', label: 'Today (since midnight ET)', summary: 'today' },
    {
        value: 'trailing_3d',
        label: 'Trailing 3 days',
        summary: 'trailing 3d',
    },
    {
        value: 'trailing_7d',
        label: 'Trailing 7 days',
        summary: 'trailing 7d',
    },
    {
        value: 'trailing_14d',
        label: 'Trailing 14 days',
        summary: 'trailing 14d',
    },
];

// Bucket size / feed cap for a window selection; custom ranges derive them
// from their span via the same thresholds as the backend.
export function outreachBucketMs(selection: OutreachWindowSelection): number {
    return OUTREACH_BUCKET_MS_BY_WINDOW[scalePreset(selection)];
}

export function outlierFeedCap(selection: OutreachWindowSelection): number {
    return OUTLIER_FEED_CAP_BY_WINDOW[scalePreset(selection)];
}

// Compact ET-labeled phrase for a custom range ("Jul 1, 9:00 AM – Jul 3,
// 5:00 PM ET"), reused by the summary below and the header inputs.
export function formatOutreachCustomRange(range: OutreachCustomRange): string {
    const format = 'MMM d, h:mm a';
    const start = formatInTimeZone(
        new Date(range.start),
        OUTREACH_ANALYTICS_TIMEZONE,
        format,
    );
    const end = formatInTimeZone(
        new Date(range.end),
        OUTREACH_ANALYTICS_TIMEZONE,
        format,
    );
    return `${start} – ${end} ET`;
}

// Compact window phrase reused in inline copy across the hero and every tab.
// Falls back to the raw value if an option is missing.
export function outreachWindowSummary(
    selection: OutreachWindowSelection,
): string {
    if (isCustomOutreachRange(selection)) {
        return formatOutreachCustomRange(selection);
    }
    return (
        OUTREACH_LIVE_WINDOW_OPTIONS.find(
            (option) => option.value === selection,
        )?.summary ?? selection
    );
}

// The window a dashboard response actually reflects — the echoed preset or
// echoed custom bounds — so labels and drill-downs follow the fetched data
// rather than an in-flight selector change. Null when the response predates
// the echo fields.
export function echoedOutreachWindow(response: {
    readonly window: OutreachLiveWindow | null;
    readonly window_start?: string | null;
    readonly window_end?: string | null;
}): OutreachWindowSelection | null {
    if (response.window) {
        return response.window;
    }
    if (response.window_start && response.window_end) {
        return { start: response.window_start, end: response.window_end };
    }
    return null;
}
